import { useCallback } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useCADPageStore } from '@/state'
import { ASMTreeNode, RawAssemblyTree } from '@/state'

const isNodeExpanded = (
  tree: RawAssemblyTree,
  node: ASMTreeNode,
  expandedNodesUUIDs: string[],
) => {
  return (
    node.parent === tree.root || expandedNodesUUIDs.includes(node.parent ?? '')
  )
}

export const useTreeNodeRangeSelector = () => {
  const selectedParts = useCADPageStore((state) => state.selectedParts)
  const expandedAssemblyTreeNodes = useCADPageStore(
    (state) => state.expandedAssemblyTreeNodes,
  )

  const pushSelectedParts = useCADPageStore(
    useShallow((state) => state.pushSelectedPart),
  )

  return useCallback(
    (tree: RawAssemblyTree, node: ASMTreeNode) => {
      const lastSelectNodeUUID =
        selectedParts.length > 0
          ? selectedParts[selectedParts.length - 1]
          : null

      if (lastSelectNodeUUID) {
        // High level range select algo
        // - Topologically sort the tree nodes
        // - Use the sorted tree nodes the find the indexes of the range
        // - Use those indexes to filter the range of sorted tree nodes
        // - Update the state

        const startIndex = tree.nodes.findIndex(
          (n) => n.uuid === lastSelectNodeUUID,
        )
        const endIndex = tree.nodes.findIndex((n) => n.uuid === node.uuid)

        const bothNodesExist = startIndex !== -1 && endIndex !== -1

        if (bothNodesExist) {
          const [lo, hi] =
            startIndex < endIndex
              ? [startIndex, endIndex]
              : [endIndex, startIndex]
          const nodeRange = tree.nodes.slice(lo, hi + 1)

          const expandedNodeRange = nodeRange.filter((n) =>
            isNodeExpanded(tree, n, expandedAssemblyTreeNodes),
          )

          expandedNodeRange.forEach((node) => pushSelectedParts(node.uuid))
        }
      } else {
        pushSelectedParts(node.uuid)
      }
    },
    [expandedAssemblyTreeNodes, pushSelectedParts, selectedParts],
  )
}
